import React, { Context, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserState } from './user-state.provider.ts';

interface SavedStatusContextType {
    savedVideoIds: Set<string>;
    isSavedLoading: boolean;
    isSaved: (videoId?: string | null) => boolean;
    refreshSaved: () => Promise<void>;
    addSaved: (videoId: string) => void;
    removeSaved: (videoId: string) => void;
}

interface SavedStatusProviderProps {
    children: JSX.Element | JSX.Element[];
}

const SavedStatusContext: Context<SavedStatusContextType> = createContext<SavedStatusContextType>(null!);

/**
 * Provider for persisted saved video ids.
 */
const SavedStatusProvider = ({ children }: SavedStatusProviderProps) => {
    const { isLoggedIn, isUserStateLoading, getUserInfo } = useUserState();
    const [savedVideoIds, setSavedVideoIds] = useState<Set<string>>(new Set());
    const [isSavedLoading, setIsSavedLoading] = useState(false);
    const isMountedRef = useRef(true);

    const loadSavedIds = useCallback(async () => {
        const userInfo = getUserInfo();
        if (!isLoggedIn || !userInfo?.token) {
            if (isMountedRef.current) {
                setSavedVideoIds(new Set());
                setIsSavedLoading(false);
            }
            return;
        }

        if (isMountedRef.current) {
            setIsSavedLoading(true);
        }

        try {
            const { fetchSavedVideoIds } = await import('../services/library.service');
            const response = await fetchSavedVideoIds(userInfo.token);
            if (isMountedRef.current) {
                setSavedVideoIds(new Set(response.VideoIds ?? []));
            }
        } catch (error) {
            if (isMountedRef.current) {
                setSavedVideoIds(new Set());
            }
        } finally {
            if (isMountedRef.current) {
                setIsSavedLoading(false);
            }
        }
    }, [getUserInfo, isLoggedIn]);

    useEffect(() => {
        isMountedRef.current = true;

        const syncSaved = async () => {
            if (isUserStateLoading) {
                return;
            }

            await loadSavedIds();
        };

        syncSaved();

        return () => {
            isMountedRef.current = false;
        };
    }, [isUserStateLoading, loadSavedIds]);

    const isSaved = useCallback((videoId?: string | null) => {
        if (!videoId) {
            return false;
        }
        return savedVideoIds.has(videoId);
    }, [savedVideoIds]);

    const addSaved = useCallback((videoId: string) => {
        if (!videoId) {
            return;
        }
        setSavedVideoIds((prev) => {
            if (prev.has(videoId)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(videoId);
            return next;
        });
    }, []);

    const removeSaved = useCallback((videoId: string) => {
        if (!videoId) {
            return;
        }
        setSavedVideoIds((prev) => {
            if (!prev.has(videoId)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(videoId);
            return next;
        });
    }, []);

    const value = useMemo(() => ({
        savedVideoIds,
        isSavedLoading,
        isSaved,
        refreshSaved: loadSavedIds,
        addSaved,
        removeSaved
    }), [addSaved, isSaved, isSavedLoading, loadSavedIds, removeSaved, savedVideoIds]);

    return (
        <SavedStatusContext.Provider value={value}>
            {children}
        </SavedStatusContext.Provider>
    );
};

/**
 * Access saved video status state.
 */
export const useSavedStatus = () => {
    return useContext(SavedStatusContext);
};

export default SavedStatusProvider;
